import { useEffect, useRef, useState } from "react";
import { ArrowRight, CheckCircle2, Circle, Clock, Star } from "lucide-react";
import type {
  WeeklyMissionPayload,
  WeeklyMissionTask,
  MissionAction,
} from "../../../api";
import {
  missionAction,
  missionProgressLabel,
  missionTaskTitle,
  sortMissionTasks,
} from "../../../components/missionModel";
import { dimensionFor } from "./dimensions";
interface Props {
  initialTaskId?: string;
  initialDimension?: string;
  mission: WeeklyMissionPayload | null;
  busy: Record<string, boolean>;
  saving: boolean;
  onComplete: (task: WeeklyMissionTask) => void;
  onOpenAction?: (action: MissionAction) => void;
}
export default function MissionWorkspace({
  initialTaskId,
  initialDimension,
  mission,
  busy,
  saving,
  onComplete,
  onOpenAction,
}: Props) {
  const detailRef = useRef<HTMLElement>(null);
  const [status, setStatus] = useState<"pending" | "completed">("pending");
  const [dimension, setDimension] = useState(initialDimension || "all");
  const [selected, setSelected] = useState<string | number | null>(
    initialTaskId || null,
  );
  const initialTask = mission?.groups.flatMap(g => g.tasks).find(t => String(t.id) === initialTaskId);
  const initialStatus = initialTask?.status;
  const appliedEntry = useRef('');
  useEffect(() => {
    const entry = `${initialDimension || 'all'}:${initialTaskId || ''}`;
    if (initialTaskId && !initialStatus) return;
    if (appliedEntry.current === entry) return;
    appliedEntry.current = entry;
    setSelected(initialTaskId || null);
    setStatus(initialStatus || 'pending');
    setDimension(initialDimension || 'all');
  }, [initialTaskId, initialDimension, initialStatus]);
  const tasks = sortMissionTasks(
    (mission?.groups || []).flatMap((g) =>
      g.tasks.map((t) => ({ ...t, dimension: t.dimension || g.key })),
    ),
  );
  const filtered = tasks.filter(
    (t) =>
      t.status === status && (dimension === "all" || t.dimension === dimension),
  );
  const task =
    filtered.find((t) => String(t.id) === String(selected)) || filtered[0];
  const d = task ? dimensionFor(task.dimension) : null;
  const Icon = d?.icon || Circle;
  const open = task ? missionAction(task) : null;
  return (
    <div className="desktop-mission-workspace">
      <section className="desktop-mission-list" aria-label="本周任务列表">
        <div className="desktop-list-toolbar">
          <div className="desktop-tabs">
            {(["pending", "completed"] as const).map((s) => (
              <button
                key={s}
                aria-pressed={status === s}
                onClick={() => setStatus(s)}
              >
                {s === "pending" ? "未完成" : "已完成"}{" "}
                <span>{tasks.filter((t) => t.status === s).length}</span>
              </button>
            ))}
          </div>
          <select
            aria-label="按维度筛选任务"
            value={dimension}
            onChange={(e) => setDimension(e.target.value)}
          >
            <option value="all">全部维度</option>
            {mission?.groups.map((g) => (
              <option key={g.key} value={g.key}>
                {dimensionFor(g.key).glyph} · {dimensionFor(g.key).name}
              </option>
            ))}
          </select>
        </div>
        <div className="desktop-task-table-head" aria-hidden="true">
          <span>任务</span>
          <span>维度</span>
          <span>进度</span>
          <span>预计用时</span>
        </div>
        {filtered.map((t) => {
          const dim = dimensionFor(t.dimension);
          const DimIcon = dim.icon;
          return (
            <button
              key={t.id}
              className={`ms-task desktop-task-row ${t.status === "completed" ? "done" : ""} ${task?.id === t.id ? "selected" : ""}`}
              aria-pressed={task?.id === t.id}
              onClick={() => {
                setSelected(t.id);
                if (window.matchMedia("(max-width: 1000px)").matches)
                  detailRef.current?.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                  });
              }}
            >
              <span className="desktop-task-name">
                {t.status === "completed" ? (
                  <CheckCircle2 size={19} />
                ) : (
                  <Circle size={19} />
                )}
                <span>
                  <strong className="ms-task-title">
                    {missionTaskTitle(t)}
                  </strong>
                  <small>{t.description}</small>
                </span>
              </span>
              <span
                className="desktop-task-dimension"
                style={{ color: dim.color }}
              >
                <DimIcon size={18} />
                {dim.glyph}
              </span>
              <span className="desktop-task-progress">
                {missionProgressLabel(t)}
              </span>
              <span className="desktop-task-minutes">
                {t.estimated_minutes ? `${t.estimated_minutes} 分钟` : "—"}
              </span>
            </button>
          );
        })}
        {!filtered.length && (
          <div className="desktop-empty">
            <CheckCircle2 size={28} />
            <p>
              {status === "pending"
                ? "当前筛选下没有待完成任务"
                : "当前筛选下还没有已完成任务"}
            </p>
            {dimension !== "all" && (
              <button
                className="desktop-text-button"
                onClick={() => setDimension("all")}
              >
                查看全部维度
              </button>
            )}
          </div>
        )}
      </section>
      <aside
        ref={detailRef}
        className="desktop-task-detail"
        aria-label="任务详情"
      >
        {task && d ? (
          <>
            <div className="desktop-task-kicker" style={{ color: d.color }}>
              <span
                className="desktop-dimension-icon"
                style={{ background: d.background }}
              >
                <Icon size={24} />
              </span>
              {d.glyph} · {d.name}
              {task.is_boost ? " · 本周重点" : ""}
            </div>
            <h2>{missionTaskTitle(task)}</h2>
            <h3>任务说明</h3>
            <p>{task.description || "按自己的节奏完成这项行动。"}</p>
            <dl>
              <div>
                <dt>当前进度</dt>
                <dd>
                  {missionProgressLabel(task)}
                  {task.progress && (
                    <progress
                      aria-label={`${missionTaskTitle(task)}进度`}
                      value={Math.max(0, Math.min(100, task.progress.percent))}
                      max={100}
                      aria-valuenow={Math.max(
                        0,
                        Math.min(100, task.progress.percent),
                      )}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  )}
                </dd>
              </div>
              <div>
                <dt>预计用时</dt>
                <dd>
                  <Clock size={17} />
                  {task.estimated_minutes
                    ? `${task.estimated_minutes} 分钟`
                    : "按自己的节奏"}
                </dd>
              </div>
              {task.exp != null && (
                <div>
                  <dt>奖励</dt>
                  <dd className="desktop-reward">
                    <Star size={20} />+{task.exp} EXP
                  </dd>
                </div>
              )}
            </dl>
            {task.status === "completed" ? (
              <p className="desktop-completed">
                <CheckCircle2 size={19} />
                {task.archived ? "此前已完成" : "已完成"}
              </p>
            ) : (
              !task.archived &&
              (task.verification === "auto" ? (
                <button
                  className="desktop-primary"
                  disabled={
                    saving || !onOpenAction || !open || open.type === "manual"
                  }
                  onClick={() => open && onOpenAction?.(open)}
                >
                  {open?.label}
                  <ArrowRight size={18} />
                </button>
              ) : (
                <button
                  className="desktop-primary"
                  disabled={saving || busy[`weekly:${task.id}`]}
                  onClick={() => onComplete(task)}
                >
                  {busy[`weekly:${task.id}`] ? "保存中…" : "确认完成"}
                  <CheckCircle2 size={18} />
                </button>
              ))
            )}
            {task.reason && (
              <details className="desktop-task-reason" open>
                <summary>推荐理由</summary>
                <p>{task.reason}</p>
              </details>
            )}
          </>
        ) : (
          <div className="desktop-empty">
            <Circle size={28} />
            <p>从左侧选择一项任务查看详情</p>
          </div>
        )}
      </aside>
    </div>
  );
}
